import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './PayrollExport.css';

const PayrollExport = () => {
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [payrolls, setPayrolls] = useState([]);
    const [payComponents, setPayComponents] = useState([]);
    const [employeesData, setEmployeesData] = useState([]);
    // REMOVED: MAX_PERSONAL_TEMPLATES constant

    const [selectedPayroll, setSelectedPayroll] = useState('');
    const [viewMode, setViewMode] = useState('list');
    const [selectedComponents, setSelectedComponents] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [searchField, setSearchField] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null);
    const [payrollInfo, setPayrollInfo] = useState(null);

    // Add template-related states here
    const [savedTemplates, setSavedTemplates] = useState([]);
    const [templateName, setTemplateName] = useState('');
    const [currentTemplateId, setCurrentTemplateId] = useState(null);
    const [saveAsDefault, setSaveAsDefault] = useState(false);
    const [saveAsCompanyDefault, setSaveAsCompanyDefault] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);

    // Add alert states
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState(''); // 'success' or 'error'
    const [showAlert, setShowAlert] = useState(false);

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
    const previewRef = useRef(null);
    const alertTimeoutRef = useRef(null);
    const initialLoadRef = useRef(false);

    // Show alert function
    const showAlertMessage = (message, type = 'success', duration = 3000) => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);

        // Clear any existing timeout
        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current);
        }

        // Auto-hide alert
        alertTimeoutRef.current = setTimeout(() => {
            setShowAlert(false);
        }, duration);
    };

    // Close alert manually
    const closeAlert = () => {
        setShowAlert(false);
        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current);
        }
    };

    // Fetch payroll masters
    const fetchPayrolls = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}payroll/get_payrolls.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setPayrolls(response.data.payrolls || []);
            }
        } catch (error) {
            console.error('Error fetching payrolls:', error);
        }
    }, [API_BASE]);

    // Fetch pay components
    const fetchPayComponents = useCallback(async (companyId = null) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}payroll/get_components.php`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: companyId }
            });
            if (response.data.success) {
                const components = response.data.components || [];
                setPayComponents(components);

                // Only auto-select default components if no template is loaded
                if (!currentTemplateId && selectedComponents.length === 0) {
                    const defaultComponents = ['Basic', 'DA', 'HRA', 'PF', 'ESI', 'PF_Ceiling', 'ESI_Ceiling'];
                    const toSelect = components
                        .filter(comp => defaultComponents.includes(comp.component_name))
                        .map(comp => comp.component_name);

                    setSelectedComponents(toSelect);
                }
            }
        } catch (error) {
            console.error('Error fetching pay components:', error);
        }
    }, [API_BASE, currentTemplateId, selectedComponents.length]);

    // Fetch saved templates
    const fetchTemplates = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}payroll/save_report_template.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setSavedTemplates(response.data.templates || []);
            } else {
                console.error('Error in template response:', response.data);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    }, [API_BASE]);

    // REMOVED: checkTemplateLimit function as template limit is no longer enforced

    // Update the save template function - removed template limit check
    const saveTemplate = async (isUpdate = false) => {
        if (!templateName.trim()) {
            showAlertMessage('Please enter a template name', 'error');
            return;
        }

        // REMOVED: Template limit check - users can now save any number of templates

        try {
            const token = localStorage.getItem('authToken');
            const url = `${API_BASE}payroll/save_report_template.php`;

            const payload = {
                template_name: templateName,
                selectedComponents: selectedComponents,
                view_mode: viewMode,
                output_format: 'pdf',
                is_default: saveAsDefault,
                is_company_default: saveAsCompanyDefault
            };

            let response;
            if (isUpdate && currentTemplateId) {
                payload.template_id = currentTemplateId;
                response = await axios.put(url, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            } else {
                response = await axios.post(url, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }

            if (response.data.success) {
                showAlertMessage(isUpdate ? 'Template updated successfully!' : 'Template saved successfully!');
                setTemplateName('');
                setSaveAsDefault(false);
                setSaveAsCompanyDefault(false);
                setShowSaveDialog(false);
                if (!isUpdate) setCurrentTemplateId(response.data.template_id);
                fetchTemplates();
            } else {
                showAlertMessage('Error saving template: ' + response.data.error, 'error');
            }
        } catch (error) {
            console.error('Error saving template:', error);
            showAlertMessage('Error saving template: ' + (error.response?.data?.error || error.message), 'error');
        }
    };

    // Fetch payroll preview data
    const fetchPreview = useCallback(async (shouldScroll = true) => {
        if (!selectedPayroll) {
            showAlertMessage('Please select a payroll first', 'error');
            return;
        }

        setPreviewLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}payroll/export_payroll.php`,
                {
                    payroll_id: selectedPayroll,
                    selectedComponents: selectedComponents,
                    view: viewMode,
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setEmployeesData(response.data.employees || []);
                setCompanyInfo(response.data.company || {});
                setPayrollInfo(response.data.payroll_info || {});
                setShowPreview(true);

                // Scroll to preview only if requested
                if (shouldScroll) {
                    setTimeout(() => {
                        if (previewRef.current) {
                            previewRef.current.scrollIntoView({ behavior: 'smooth' });
                        }
                    }, 100);
                }
            } else {
                showAlertMessage(`Error fetching preview: ${response.data.message || JSON.stringify(response.data)}`, 'error');
            }
        } catch (error) {
            console.error('Error fetching preview:', error);
            showAlertMessage(`Error fetching preview: ${error.message}${error.response ? ' - ' + JSON.stringify(error.response.data) : ''}`, 'error');
        } finally {
            setPreviewLoading(false);
        }
    }, [API_BASE, selectedPayroll, selectedComponents, viewMode]);

    // FIXED: Load template function
    const loadTemplate = useCallback(async (templateId) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}payroll/save_report_template.php`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { template_id: templateId, action: 'get_template' }
            });

            if (response.data.success && response.data.template) {
                const template = response.data.template;

                // Handle different template data formats
                let selectedComponentsArray = [];

                if (template.selected_components) {
                    // If it's a string, parse it
                    if (typeof template.selected_components === 'string') {
                        try {
                            selectedComponentsArray = JSON.parse(template.selected_components);
                        } catch (e) {
                            console.warn('Template components not in JSON format, using as-is');
                            // Try to parse comma-separated string
                            if (template.selected_components.includes(',')) {
                                selectedComponentsArray = template.selected_components.split(',').map(item => item.trim());
                            } else if (template.selected_components.includes('[')) {
                                // Try to extract array from string
                                const match = template.selected_components.match(/\[(.*?)\]/);
                                if (match) {
                                    selectedComponentsArray = match[1].split(',').map(item => item.trim().replace(/['"]/g, ''));
                                } else {
                                    selectedComponentsArray = [template.selected_components];
                                }
                            } else {
                                selectedComponentsArray = [template.selected_components];
                            }
                        }
                    } else if (Array.isArray(template.selected_components)) {
                        selectedComponentsArray = template.selected_components;
                    }
                }

                // Ensure we have valid component names
                const validComponents = selectedComponentsArray
                    .filter(comp => comp && typeof comp === 'string' && comp.trim() !== '')
                    .map(comp => comp.trim());

                console.log('Loading template components:', validComponents); // Debug log

                setSelectedComponents(validComponents);
                setViewMode(template.view_mode || 'list');
                setCurrentTemplateId(template.template_id || template.id);

                showAlertMessage('Template loaded successfully!');

                // Optionally: If a payroll is selected, automatically load preview (without auto-scrolling)
                if (selectedPayroll) {
                    setTimeout(() => {
                        fetchPreview(false);
                    }, 500);
                }
            } else {
                showAlertMessage('Error loading template: Invalid template data', 'error');
            }
        } catch (error) {
            console.error('Error loading template:', error);
            showAlertMessage('Error loading template: ' + (error.response?.data?.error || error.message), 'error');
        }
    }, [API_BASE, selectedPayroll, fetchPreview]);

    // Delete template function
    const deleteTemplate = useCallback(async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template?')) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.delete(`${API_BASE}payroll/save_report_template.php`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { template_id: templateId, action: 'delete' }
            });

            if (response.data.success) {
                showAlertMessage('Template deleted successfully!');
                fetchTemplates();
                if (currentTemplateId === templateId) {
                    setCurrentTemplateId(null);
                    // Reset to default selection if current template was deleted
                    const defaultComponents = ['Basic', 'DA', 'HRA', 'PF', 'ESI', 'PF_Ceiling', 'ESI_Ceiling'];
                    setSelectedComponents(prev =>
                        prev.filter(comp => defaultComponents.includes(comp))
                    );
                }
            } else {
                showAlertMessage('Error deleting template: ' + response.data.error, 'error');
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            showAlertMessage('Error deleting template: ' + (error.response?.data?.error || error.message), 'error');
        }
    }, [API_BASE, currentTemplateId, fetchTemplates]);

    // Load company default template on component mount
    const loadCompanyDefaultTemplate = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}payroll/save_report_template.php`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { action: 'get_default' }
            });

            if (response.data.success && response.data.template) {
                const template = response.data.template;
                await loadTemplate(template.template_id || template.id);
            }
        } catch (error) {
            console.error('Error loading default template:', error);
        }
    }, [API_BASE, loadTemplate]);

    useEffect(() => {
        if (!initialLoadRef.current) {
            fetchPayrolls();
            fetchPayComponents();
            fetchTemplates();
            // Load default template after a short delay
            setTimeout(() => {
                loadCompanyDefaultTemplate();
            }, 1000);
            initialLoadRef.current = true;
        }

        // Cleanup alert timeout on unmount
        return () => {
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current);
            }
        };
    }, [fetchPayrolls, fetchPayComponents, fetchTemplates, loadCompanyDefaultTemplate]);

    // When payroll is selected, fetch components for that company
    useEffect(() => {
        if (selectedPayroll) {
            const selected = payrolls.find(p => String(p.payroll_id) === String(selectedPayroll));
            if (selected && selected.company_id) {
                fetchPayComponents(selected.company_id);
            }
        }
    }, [selectedPayroll, payrolls, fetchPayComponents]);

    const handleComponentSelection = (componentId) => {
        setSelectedComponents(prev =>
            prev.includes(componentId)
                ? prev.filter(id => id !== componentId)
                : [...prev, componentId]
        );
    };

    const handleSelectAll = (category) => {
        const categoryComponents = payComponents
            .filter(comp => comp.component_type === category)
            .map(comp => comp.component_name);

        const allSelected = categoryComponents.every(comp => selectedComponents.includes(comp));

        if (allSelected) {
            setSelectedComponents(prev => prev.filter(comp => !categoryComponents.includes(comp)));
        } else {
            setSelectedComponents(prev => {
                const newComps = [...prev];
                categoryComponents.forEach(comp => {
                    if (!newComps.includes(comp)) {
                        newComps.push(comp);
                    }
                });
                return newComps;
            });
        }
    };

    const handleSelectAllComponents = () => {
        const allComponentNames = payComponents.map(comp => comp.component_name);

        if (selectedComponents.length === allComponentNames.length) {
            setSelectedComponents([]);
        } else {
            setSelectedComponents([...allComponentNames]);
        }
    };

    // Calculate totals for summary
    const calculateSummary = (employeePayroll) => {
        if (!employeePayroll) return { totalEarnings: 0, totalDeductions: 0, netPay: 0 };

        const earnings = payComponents
            .filter(comp => comp.component_type === 'Earning' && selectedComponents.includes(comp.component_name))
            .reduce((sum, comp) => {
                const value = parseFloat(employeePayroll[comp.component_name] || 0);
                return sum + (isNaN(value) ? 0 : value);
            }, 0);

        const deductions = payComponents
            .filter(comp => comp.component_type === 'Deduction' && selectedComponents.includes(comp.component_name))
            .reduce((sum, comp) => {
                const value = parseFloat(employeePayroll[comp.component_name] || 0);
                return sum + (isNaN(value) ? 0 : value);
            }, 0);

        return {
            totalEarnings: earnings.toFixed(2),
            totalDeductions: deductions.toFixed(2),
            netPay: (earnings - deductions).toFixed(2)
        };
    };

    // Export to PDF
    const exportToPDF = async () => {
        if (!selectedPayroll) {
            showAlertMessage('Please select a payroll first', 'error');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');

            // First get the data
            const response = await axios.post(`${API_BASE}payroll/export_payroll.php`,
                {
                    payroll_id: selectedPayroll,
                    selectedComponents: selectedComponents,
                    view: 'list',
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const { company, payroll_info, employees } = response.data;

                // Create PDF
                const doc = new jsPDF({
                    orientation: 'landscape',
                    unit: 'mm',
                    format: 'a4'
                });

                // Company Header
                doc.setFontSize(16);
                doc.setTextColor(40, 40, 40);
                doc.text(company?.company_name || 'Payroll Report', 14, 20);

                doc.setFontSize(10);
                doc.setTextColor(100, 100, 100);
                doc.text(`Payroll Report - ${payroll_info.month_year}`, 14, 28);
                doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 34);

                // Company Details
                if (company?.address) {
                    doc.text(`Address: ${company.address}, ${company.city || ''}, ${company.state || ''}`, 14, 42);
                }
                if (company?.phone) {
                    doc.text(`Phone: ${company.phone} | Email: ${company.email || ''}`, 14, 48);
                }

                // Payroll Info
                doc.text(`Period: ${payroll_info.from_date} to ${payroll_info.to_date} | Status: ${payroll_info.status}`, 14, 56);

                // Prepare table data
                const headers = [
                    'Emp Code',
                    'Employee Name',
                    'Department',
                    'Designation',
                    ...selectedComponents,
                    'Total Earnings',
                    'Total Deductions',
                    'Net Pay'
                ];

                const tableData = employees.map(emp => {
                    const summary = calculateSummary(emp);
                    return [
                        emp.emp_code || '',
                        emp.name || '',
                        emp.department || '',
                        emp.designation || '',
                        ...selectedComponents.map(comp => parseFloat(emp[comp] || 0).toFixed(2)),
                        summary.totalEarnings,
                        summary.totalDeductions,
                        summary.netPay
                    ];
                });

                // Add totals row
                const totals = [
                    '',
                    'TOTALS',
                    '',
                    '',
                    ...selectedComponents.map(comp =>
                        employees.reduce((sum, emp) => sum + parseFloat(emp[comp] || 0), 0).toFixed(2)
                    ),
                    employees.reduce((sum, emp) => {
                        const summary = calculateSummary(emp);
                        return sum + parseFloat(summary.totalEarnings);
                    }, 0).toFixed(2),
                    employees.reduce((sum, emp) => {
                        const summary = calculateSummary(emp);
                        return sum + parseFloat(summary.totalDeductions);
                    }, 0).toFixed(2),
                    employees.reduce((sum, emp) => {
                        const summary = calculateSummary(emp);
                        return sum + parseFloat(summary.netPay);
                    }, 0).toFixed(2)
                ];
                tableData.push(totals);

                // Add table
                doc.autoTable({
                    head: [headers],
                    body: tableData,
                    startY: 65,
                    styles: {
                        fontSize: 7,
                        cellPadding: 1.5,
                        overflow: 'linebreak'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        fontSize: 8
                    },
                    alternateRowStyles: {
                        fillColor: [245, 245, 245]
                    },
                    columnStyles: {
                        0: { cellWidth: 20 },
                        1: { cellWidth: 35 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 25 },
                        ...selectedComponents.reduce((styles, comp, index) => {
                            styles[4 + index] = { cellWidth: 15, halign: 'right' };
                            return styles;
                        }, {}),
                        [4 + selectedComponents.length]: { cellWidth: 20, halign: 'right' },
                        [5 + selectedComponents.length]: { cellWidth: 20, halign: 'right' },
                        [6 + selectedComponents.length]: { cellWidth: 20, halign: 'right' }
                    },
                    margin: { top: 65 }
                });

                // Footer
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.setTextColor(150, 150, 150);
                    doc.text(
                        `Page ${i} of ${pageCount} | Total Employees: ${employees.length}`,
                        doc.internal.pageSize.width - 60,
                        doc.internal.pageSize.height - 10
                    );
                }

                // Save PDF
                doc.save(`payroll_report_${payroll_info.month_year.replace('/', '-')}_${new Date().toISOString().split('T')[0]}.pdf`);
                showAlertMessage('PDF exported successfully!');
            } else {
                showAlertMessage(`Error fetching data for PDF: ${response.data.message || JSON.stringify(response.data)}`, 'error');
            }
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            showAlertMessage(`Error generating PDF: ${error.message}${error.response ? ' - ' + JSON.stringify(error.response.data) : ''}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Export to Excel
    const exportToExcel = async () => {
        if (!selectedPayroll) {
            showAlertMessage('Please select a payroll first', 'error');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');

            // First get the data
            const response = await axios.post(`${API_BASE}payroll/export_payroll.php`,
                {
                    payroll_id: selectedPayroll,
                    selectedComponents: selectedComponents,
                    view: 'list',
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const { company, payroll_info, employees } = response.data;

                // Prepare data for Excel
                const excelData = [
                    // Company Info
                    [`Company: ${company.company_name}`],
                    [`Address: ${company.address || ''}`],
                    [`City: ${company.city || ''}, State: ${company.state || ''}`],
                    [`Phone: ${company.phone || ''}, Email: ${company.email || ''}`],
                    [''],
                    [`Payroll Report - ${payroll_info.month_year}`],
                    [`Period: ${payroll_info.from_date} to ${payroll_info.to_date}`],
                    ['']
                ];

                // Add headers
                const headers = [
                    'Emp Code',
                    'Employee Name',
                    'Department',
                    'Designation',
                    ...selectedComponents,
                    'Total Earnings',
                    'Total Deductions',
                    'Net Pay'
                ];
                excelData.push(headers);

                // Add employee data
                employees.forEach(emp => {
                    const summary = calculateSummary(emp);
                    const row = [
                        emp.emp_code || '',
                        emp.name || '',
                        emp.department || '',
                        emp.designation || '',
                        ...selectedComponents.map(comp => parseFloat(emp[comp] || 0).toFixed(2)),
                        summary.totalEarnings,
                        summary.totalDeductions,
                        summary.netPay
                    ];
                    excelData.push(row);
                });

                // Add totals row
                const totals = [
                    '',
                    'TOTALS',
                    '',
                    '',
                    ...selectedComponents.map(comp =>
                        employees.reduce((sum, emp) => sum + parseFloat(emp[comp] || 0), 0).toFixed(2)
                    ),
                    employees.reduce((sum, emp) => {
                        const summary = calculateSummary(emp);
                        return sum + parseFloat(summary.totalEarnings);
                    }, 0).toFixed(2),
                    employees.reduce((sum, emp) => {
                        const summary = calculateSummary(emp);
                        return sum + parseFloat(summary.totalDeductions);
                    }, 0).toFixed(2),
                    employees.reduce((sum, emp) => {
                        const summary = calculateSummary(emp);
                        return sum + parseFloat(summary.netPay);
                    }, 0).toFixed(2)
                ];
                excelData.push(totals);

                // Create workbook
                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.aoa_to_sheet(excelData);

                // Style header row (row index 8 for headers)
                const range = XLSX.utils.decode_range(ws['!ref']);
                const headerRowIndex = 8; // Headers are in row 9 (0-indexed 8)
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const address = XLSX.utils.encode_cell({ r: headerRowIndex, c: C });
                    if (!ws[address]) continue;
                    ws[address].s = {
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        fill: { fgColor: { rgb: "2980B9" } },
                        alignment: { horizontal: "center" }
                    };
                }

                // Style totals row (last row)
                const totalsRowIndex = headerRowIndex + employees.length + 1;
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const address = XLSX.utils.encode_cell({ r: totalsRowIndex, c: C });
                    if (!ws[address]) continue;
                    ws[address].s = {
                        font: { bold: true },
                        fill: { fgColor: { rgb: "F1F8E9" } }
                    };
                }

                // Set column widths
                const colWidths = [
                    { wch: 12 }, // Emp Code
                    { wch: 25 }, // Name
                    { wch: 20 }, // Department
                    { wch: 20 }, // Designation
                    ...selectedComponents.map(() => ({ wch: 15 })), // Components
                    { wch: 15 }, // Total Earnings
                    { wch: 15 }, // Total Deductions
                    { wch: 15 }  // Net Pay
                ];
                ws['!cols'] = colWidths;

                // Add worksheet to workbook
                XLSX.utils.book_append_sheet(wb, ws, 'Payroll Report');

                // Save file
                const fileName = `payroll_report_${payroll_info.month_year.replace('/', '-')}_${new Date().toISOString().split('T')[0]}.xlsx`;
                XLSX.writeFile(wb, fileName);
                showAlertMessage('Excel exported successfully!');
            } else {
                showAlertMessage(`Error fetching data for Excel: ${response.data.message || JSON.stringify(response.data)}`, 'error');
            }
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            showAlertMessage(`Error generating Excel: ${error.message}${error.response ? ' - ' + JSON.stringify(error.response.data) : ''}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const printReport = async () => {
        if (!selectedPayroll) {
            showAlertMessage('Please select a payroll first', 'error');
            return;
        }

        const printWindow = window.open('', '_blank');
        setLoading(true);

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}payroll/export_payroll.php`,
                {
                    payroll_id: selectedPayroll,
                    selectedComponents: selectedComponents,
                    view: 'list',
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const { company, payroll_info, employees } = response.data;

                let printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Payroll Report - ${payroll_info.month_year}</title>
            <style>
              @media print {
                body { font-family: Arial, sans-serif; margin: 15mm; font-size: 11px; }
                .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 15px; }
                .company-name { font-size: 20px; font-weight: bold; color: #2c3e50; }
                .payroll-title { font-size: 16px; color: #34495e; margin: 10px 0; }
                .company-info { font-size: 10px; color: #7f8c8d; margin-bottom: 10px; }
                .payroll-info { font-size: 10px; color: #95a5a6; margin-bottom: 15px; }
                table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 9px; }
                th { background-color: #3498db; color: white; padding: 5px; text-align: left; border: 1px solid #ddd; }
                td { padding: 4px; border: 1px solid #ddd; }
                tr:nth-child(even) { background-color: #f8f9fa; }
                .summary { margin-top: 20px; padding: 10px; background-color: #f8f9fa; border: 1px solid #ddd; }
                .summary-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
                .summary-label { font-weight: bold; }
                .summary-value { text-align: right; }
                .footer { margin-top: 20px; text-align: center; font-size: 8px; color: #95a5a6; }
                @page { margin: 15mm; }
                .break-page { page-break-before: always; }
              }
              @media screen {
                body { font-family: Arial, sans-serif; margin: 40px; font-size: 13px; }
                .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
                .company-name { font-size: 24px; font-weight: bold; color: #2c3e50; }
                .payroll-title { font-size: 18px; color: #34495e; margin: 15px 0; }
                .company-info { font-size: 12px; color: #7f8c8d; margin-bottom: 15px; }
                .payroll-info { font-size: 12px; color: #95a5a6; margin-bottom: 20px; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11px; }
                th { background-color: #3498db; color: white; padding: 8px; text-align: left; border: 1px solid #ddd; }
                td { padding: 6px; border: 1px solid #ddd; }
                tr:nth-child(even) { background-color: #f8f9fa; }
                .summary { margin-top: 30px; padding: 15px; background-color: #f8f9fa; border: 1px solid #ddd; }
                .summary-row { display: flex; justify-content: space-between; margin-bottom: 8px; }
                .summary-label { font-weight: bold; }
                .summary-value { text-align: right; font-weight: bold; }
                .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #95a5a6; }
              }
              th, td { white-space: nowrap; }
              .text-right { text-align: right; }
              .text-center { text-align: center; }
              .bold { font-weight: bold; }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="company-name">${company?.company_name || 'Company Name'}</div>
              <div class="payroll-title">Payroll Report - ${payroll_info.month_year}</div>
              <div class="company-info">
                ${company?.address ? `<div>${company.address}, ${company.city || ''}, ${company.state || ''}</div>` : ''}
                ${company?.phone ? `<div>Phone: ${company.phone} | Email: ${company.email || ''}</div>` : ''}
              </div>
              <div class="payroll-info">
                Period: ${payroll_info.from_date} to ${payroll_info.to_date} | 
                Status: ${payroll_info.status} | 
                Generated on: ${new Date().toLocaleDateString()}
              </div>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Emp Code</th>
                  <th>Employee Name</th>
                  <th>Department</th>
                  <th>Designation</th>
                  ${selectedComponents.map(comp => `<th class="text-right">${comp}</th>`).join('')}
                  <th class="text-right">Total Earnings</th>
                  <th class="text-right">Total Deductions</th>
                  <th class="text-right">Net Pay</th>
                </tr>
              </thead>
              <tbody>
                ${employees.map((emp, index) => {
                    const summary = calculateSummary(emp);
                    return `
                    <tr>
                      <td>${emp.emp_code || ''}</td>
                      <td>${emp.name || ''}</td>
                      <td>${emp.department || ''}</td>
                      <td>${emp.designation || ''}</td>
                      ${selectedComponents.map(comp =>
                        `<td class="text-right">${parseFloat(emp[comp] || 0).toFixed(2)}</td>`
                    ).join('')}
                      <td class="text-right bold">${summary.totalEarnings}</td>
                      <td class="text-right bold text-danger">${summary.totalDeductions}</td>
                      <td class="text-right bold text-success">${summary.netPay}</td>
                    </tr>
                    ${(index + 1) % 40 === 0 ? '<tr class="break-page"></tr>' : ''}
                  `;
                }).join('')}
              </tbody>
              <tfoot>
                <tr class="bold">
                  <td colspan="4" class="text-right">TOTALS:</td>
                  ${selectedComponents.map(comp => {
                    const total = employees.reduce((sum, emp) => sum + parseFloat(emp[comp] || 0), 0);
                    return `<td class="text-right">${total.toFixed(2)}</td>`;
                }).join('')}
                  <td class="text-right">${employees.reduce((sum, emp) => {
                    const summary = calculateSummary(emp);
                    return sum + parseFloat(summary.totalEarnings);
                }, 0).toFixed(2)}</td>
                  <td class="text-right text-danger">${employees.reduce((sum, emp) => {
                    const summary = calculateSummary(emp);
                    return sum + parseFloat(summary.totalDeductions);
                }, 0).toFixed(2)}</td>
                  <td class="text-right text-success">${employees.reduce((sum, emp) => {
                    const summary = calculateSummary(emp);
                    return sum + parseFloat(summary.netPay);
                }, 0).toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
            
            <div class="summary">
              <div class="summary-row">
                <span class="summary-label">Total Employees:</span>
                <span class="summary-value">${employees.length}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Total Earnings:</span>
                <span class="summary-value">${employees.reduce((sum, emp) => {
                    const summary = calculateSummary(emp);
                    return sum + parseFloat(summary.totalEarnings);
                }, 0).toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Total Deductions:</span>
                <span class="summary-value">${employees.reduce((sum, emp) => {
                    const summary = calculateSummary(emp);
                    return sum + parseFloat(summary.totalDeductions);
                }, 0).toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Total Net Pay:</span>
                <span class="summary-value">${employees.reduce((sum, emp) => {
                    const summary = calculateSummary(emp);
                    return sum + parseFloat(summary.netPay);
                }, 0).toFixed(2)}</span>
              </div>
            </div>
            
            <div class="footer">
              <p>Payroll Report generated by HRMS | ${new Date().toLocaleString()}</p>
            </div>
            
            <script>
              window.onload = function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 500);
              }
            </script>
          </body>
          </html>
        `;

                if (printWindow) {
                    printWindow.document.write(printContent);
                    printWindow.document.close();
                }
                showAlertMessage('Print window opened successfully!');
            } else {
                showAlertMessage(`Error generating print report: ${response.data.message}`, 'error');
                if (printWindow) printWindow.close();
            }
        } catch (error) {
            console.error('Error generating print report:', error);
            showAlertMessage(`Error generating print report: ${error.message}`, 'error');
            if (printWindow) printWindow.close();
        } finally {
            setLoading(false);
        }
    };

    const filteredComponents = searchField
        ? payComponents.filter(comp =>
            comp.component_name.toLowerCase().includes(searchField.toLowerCase()) ||
            comp.component_type.toLowerCase().includes(searchField.toLowerCase())
        )
        : payComponents;

    // Get selected payroll info
    const selectedPayrollInfo = payrolls.find(p => String(p.payroll_id) === String(selectedPayroll));

    // Component type labels
    const componentTypeLabels = {
        'Earning': { label: 'Earnings', color: 'success', icon: 'bi-cash' },
        'Deduction': { label: 'Deductions', color: 'danger', icon: 'bi-dash-circle' },
        'CTC': { label: 'Cost to Company', color: 'warning', icon: 'bi-building' },
        'General': { label: 'General Components', color: 'info', icon: 'bi-gear' },
        'Global': { label: 'Global Components', color: 'primary', icon: 'bi-globe' }
    };

    return (
        <div className="container-fluid py-4 payroll-export-container">
            {/* Alert Notification */}
            {showAlert && (
                <div className={`alert alert-${alertType} alert-dismissible fade show mb-4`} role="alert">
                    <div className="d-flex align-items-center">
                        {alertType === 'success' ? (
                            <i className="bi bi-check-circle-fill me-2"></i>
                        ) : (
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        )}
                        <div>{alertMessage}</div>
                    </div>
                    <button type="button" className="btn-close" onClick={closeAlert}></button>
                </div>
            )}

            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <h2 className="h4 mb-1 fw-bold text-dark">
                                        <i className="bi bi-cash-coin me-2 text-primary"></i>
                                        Export Payroll Details
                                    </h2>
                                    <p className="text-muted mb-0">
                                        Select payroll, choose components, and generate reports
                                    </p>
                                    <small className="text-muted">
                                        Available Components: {payComponents.length} | Selected: {selectedComponents.length}
                                        {currentTemplateId && (
                                            <span className="ms-2 badge bg-info">
                                                <i className="bi bi-template me-1"></i>
                                                Template Loaded
                                            </span>
                                        )}
                                    </small>
                                </div>
                                <div className="col-md-6 text-end">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => window.history.back()}
                                    >
                                        <i className="bi bi-arrow-left me-2"></i>
                                        Back
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payroll Selection Section */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="mb-0">
                                <i className="bi bi-calendar-check me-2 text-primary"></i>
                                Select Payroll
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Payroll Period</label>
                                    <select
                                        className="form-select"
                                        value={selectedPayroll}
                                        onChange={(e) => setSelectedPayroll(e.target.value)}
                                    >
                                        <option value="">Select a payroll</option>
                                        {payrolls.map(payroll => (
                                            <option key={payroll.payroll_id} value={payroll.payroll_id}>
                                                {payroll.month_year} - {payroll.from_date} to {payroll.to_date} ({payroll.status})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">View Mode</label>
                                    <select
                                        className="form-select"
                                        value={viewMode}
                                        onChange={(e) => setViewMode(e.target.value)}
                                    >
                                        <option value="list">List View</option>
                                        <option value="card">Card View</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">&nbsp;</label>
                                    <button
                                        className="btn btn-primary w-100"
                                        onClick={fetchPreview}
                                        disabled={!selectedPayroll || previewLoading}
                                    >
                                        {previewLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Loading...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-eye me-2"></i>
                                                Load Payroll
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Selected Payroll Info */}
                            {selectedPayrollInfo && (
                                <div className="mt-3 p-3 bg-light rounded">
                                    <div className="row">
                                        <div className="col-md-4">
                                            <small className="text-muted">Payroll ID:</small>
                                            <div className="fw-bold">{selectedPayrollInfo.payroll_id}</div>
                                        </div>
                                        <div className="col-md-4">
                                            <small className="text-muted">Period:</small>
                                            <div className="fw-bold">{selectedPayrollInfo.month_year}</div>
                                        </div>
                                        <div className="col-md-4">
                                            <small className="text-muted">Status:</small>
                                            <div>
                                                <span className={`badge bg-${selectedPayrollInfo.status === 'Closed' ? 'success' : selectedPayrollInfo.status === 'Processed' ? 'warning' : 'secondary'}`}>
                                                    {selectedPayrollInfo.status}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>


            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="bi bi-save me-2 text-primary"></i>
                                    Saved Report Templates
                                    <span className="badge bg-secondary ms-2">
                                        Company-wide & Personal
                                    </span>
                                </h5>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => {
                                            const activeTemplate = savedTemplates.find(t => t.template_id === currentTemplateId);
                                            if (activeTemplate) {
                                                setTemplateName(activeTemplate.template_name);
                                            }
                                            setShowSaveDialog(true);
                                        }}
                                        disabled={selectedComponents.length === 0}
                                    >
                                        <i className="bi bi-save me-1"></i>
                                        {currentTemplateId ? 'Save/Update Template' : 'Save Current as Template'}
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={fetchTemplates}
                                    >
                                        <i className="bi bi-arrow-clockwise"></i>
                                    </button>
                                </div>
                            </div>
                            <small className="text-muted">
                                Templates are saved per company and can be reused across different payroll periods.
                            </small>
                        </div>
                        <div className="card-body">
                            {savedTemplates.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bi bi-folder-x display-6"></i>
                                    <p className="mt-2">No saved templates yet</p>
                                    <p className="small">Save your component selections to create reusable templates</p>
                                </div>
                            ) : (
                                <div className="row">
                                    {savedTemplates.map(template => (
                                        <div key={template.template_id} className="col-md-4 mb-3">
                                            <div className={`card h-100 ${currentTemplateId === template.template_id ? 'border-primary border-2' : 'border'}`}>
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h6 className="card-title mb-0 d-flex align-items-center">
                                                            {template.template_name}
                                                            {template.is_default && template.is_owner && (
                                                                <span className="badge bg-info ms-2">My Default</span>
                                                            )}
                                                            {template.is_default && !template.is_owner && (
                                                                <span className="badge bg-warning ms-2">Company Default</span>
                                                            )}
                                                        </h6>
                                                        <div className="dropdown">
                                                            <button className="btn btn-sm btn-link" type="button" data-bs-toggle="dropdown">
                                                                <i className="bi bi-three-dots-vertical"></i>
                                                            </button>
                                                            <ul className="dropdown-menu">
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item"
                                                                        onClick={() => loadTemplate(template.template_id)}
                                                                    >
                                                                        <i className="bi bi-box-arrow-in-down me-2"></i>
                                                                        Load
                                                                    </button>
                                                                </li>
                                                                <li><hr className="dropdown-divider" /></li>
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item text-danger"
                                                                        onClick={() => deleteTemplate(template.template_id)}
                                                                    >
                                                                        <i className="bi bi-trash me-2"></i>
                                                                        Delete
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>

                                                    <div className="mb-2">
                                                        <span className={`badge ${template.view_mode === 'list' ? 'bg-primary' : 'bg-info'} me-1`}>
                                                            <i className={`bi bi-${template.view_mode === 'list' ? 'list' : 'card-text'} me-1`}></i>
                                                            {template.view_mode === 'list' ? 'List' : 'Card'} View
                                                        </span>
                                                        <span className="badge bg-secondary">
                                                            <i className="bi bi-file-earmark me-1"></i>
                                                            {template.output_format.toUpperCase()}
                                                        </span>
                                                    </div>

                                                    <div className="mb-3">
                                                        <small className="text-muted">
                                                            <i className="bi bi-check-circle me-1"></i>
                                                            {template.selected_components?.length || 0} components selected
                                                        </small>
                                                        <div className="mt-1">
                                                            <small className="text-muted">
                                                                {template.is_owner ? (
                                                                    <>
                                                                        <i className="bi bi-person me-1"></i>
                                                                        Your template
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="bi bi-building me-1"></i>
                                                                        Company template
                                                                    </>
                                                                )}
                                                            </small>
                                                        </div>
                                                    </div>

                                                    <div className="mt-2 pt-2 border-top">
                                                        <small className="text-muted">
                                                            Updated: {new Date(template.updated_at).toLocaleDateString()}
                                                        </small>
                                                    </div>
                                                </div>
                                                {currentTemplateId === template.template_id && (
                                                    <div className="card-footer bg-primary bg-opacity-10 py-1">
                                                        <small className="text-primary">
                                                            <i className="bi bi-check-circle me-1"></i>
                                                            Currently loaded
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Save Template Modal */}
            {showSaveDialog && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="bi bi-save me-2"></i>
                                    Save Report Template
                                </h5>
                                <button type="button" className="btn-close" onClick={() => {
                                    setShowSaveDialog(false);
                                    setTemplateName('');
                                    setSaveAsDefault(false);
                                    setSaveAsCompanyDefault(false);
                                }}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Template Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g., Standard Payroll Report, Executive Summary, etc."
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        autoFocus
                                    />
                                    <small className="text-muted">Give a descriptive name for this template</small>
                                </div>

                                {/* REMOVED: Template Limit Warning */}

                                <div className="mb-3">
                                    <div className="form-check mb-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="saveAsDefault"
                                            checked={saveAsDefault}
                                            onChange={(e) => setSaveAsDefault(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="saveAsDefault">
                                            Set as my personal default template
                                        </label>
                                    </div>

                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="saveAsCompanyDefault"
                                            checked={saveAsCompanyDefault}
                                            onChange={(e) => {
                                                setSaveAsCompanyDefault(e.target.checked);
                                                if (e.target.checked) setSaveAsDefault(false);
                                            }}
                                        />
                                        <label className="form-check-label" htmlFor="saveAsCompanyDefault">
                                            Set as company-wide default template
                                        </label>
                                        {saveAsCompanyDefault && (
                                            <small className="text-muted d-block">
                                                <i className="bi bi-info-circle me-1"></i>
                                                Only administrators can set company defaults
                                            </small>
                                        )}
                                    </div>
                                </div>

                                <div className="alert alert-info">
                                    <i className="bi bi-info-circle me-2"></i>
                                    <div>
                                        <strong>Template Summary:</strong>
                                        <ul className="mb-0 mt-1">
                                            <li>{selectedComponents.length} components selected</li>
                                            <li>{viewMode === 'list' ? 'List' : 'Card'} view mode</li>
                                            <li>PDF export format</li>
                                            <li>Personal templates: Unlimited</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowSaveDialog(false);
                                        setTemplateName('');
                                        setSaveAsDefault(false);
                                        setSaveAsCompanyDefault(false);
                                    }}
                                >
                                    Cancel
                                </button>
                                {currentTemplateId && (
                                    <button
                                        type="button"
                                        className="btn btn-info"
                                        onClick={() => saveTemplate(true)}
                                        disabled={!templateName.trim()}
                                    >
                                        <i className="bi bi-check-circle me-2"></i>
                                        Update Existing
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => saveTemplate(false)}
                                    disabled={!templateName.trim()}
                                >
                                    <i className="bi bi-plus-circle me-2"></i>
                                    {currentTemplateId ? 'Save as New' : 'Save Template'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Component Selection Panel */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="bi bi-check2-square me-2 text-primary"></i>
                                    Select Pay Components
                                </h5>
                                <div className="d-flex align-items-center">
                                    <div className="input-group me-3" style={{ width: '300px' }}>
                                        <span className="input-group-text bg-light">
                                            <i className="bi bi-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search components..."
                                            value={searchField}
                                            onChange={(e) => setSearchField(e.target.value)}
                                        />
                                        {searchField && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                onClick={() => setSearchField('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={handleSelectAllComponents}
                                    >
                                        {selectedComponents.length === payComponents.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                            </div>
                            <small className="text-muted">
                                Selected: {selectedComponents.length} of {payComponents.length} components
                                {currentTemplateId && (
                                    <span className="ms-2 text-primary">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Template loaded
                                    </span>
                                )}
                            </small>
                        </div>
                        <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <div className="dropdown">
                                    <button className="btn btn-sm btn-outline-success dropdown-toggle" type="button" data-bs-toggle="dropdown">
                                        <i className="bi bi-folder-plus me-1"></i>
                                        Load Template
                                    </button>
                                    <ul className="dropdown-menu">
                                        {savedTemplates.map(template => (
                                            <li key={template.template_id}>
                                                <button
                                                    className="dropdown-item d-flex justify-content-between align-items-center"
                                                    onClick={() => loadTemplate(template.template_id)}
                                                >
                                                    <span>{template.template_name}</span>
                                                    {template.is_default && (
                                                        <span className="badge bg-warning">Default</span>
                                                    )}
                                                </button>
                                            </li>
                                        ))}
                                        {savedTemplates.length === 0 && (
                                            <li><span className="dropdown-item text-muted">No templates saved</span></li>
                                        )}
                                    </ul>
                                </div>
                                {currentTemplateId && (
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => {
                                            setCurrentTemplateId(null);
                                            setSelectedComponents([]);
                                            showAlertMessage('Template cleared!');
                                        }}
                                    >
                                        <i className="bi bi-x-circle me-1"></i>
                                        Clear Template
                                    </button>
                                )}
                            </div>
                            {Object.keys(componentTypeLabels).map(type => {
                                const typeComponents = filteredComponents.filter(comp => comp.component_type === type);
                                if (typeComponents.length === 0) return null;

                                const allSelected = typeComponents.every(comp => selectedComponents.includes(comp.component_name));
                                const selectedCount = typeComponents.filter(comp => selectedComponents.includes(comp.component_name)).length;
                                const typeInfo = componentTypeLabels[type];

                                return (
                                    <div key={type} className="mb-4">
                                        <div className="card border">
                                            <div className={`card-header bg-${typeInfo.color} bg-opacity-10 border-${typeInfo.color} border-bottom`}>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <h6 className="mb-0">
                                                            <i className={`bi ${typeInfo.icon} me-2 text-${typeInfo.color}`}></i>
                                                            {typeInfo.label}
                                                            <span className="badge bg-light text-dark ms-2">
                                                                {selectedCount}/{typeComponents.length}
                                                            </span>
                                                        </h6>
                                                    </div>
                                                    <button
                                                        className={`btn btn-sm btn-outline-${typeInfo.color}`}
                                                        onClick={() => handleSelectAll(type)}
                                                    >
                                                        {allSelected ? 'Deselect All' : 'Select All'}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    {typeComponents.map(comp => (
                                                        <div key={comp.component_id} className="col-md-6 mb-2">
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id={`comp-${comp.component_id}`}
                                                                    checked={selectedComponents.includes(comp.component_name)}
                                                                    onChange={() => handleComponentSelection(comp.component_name)}
                                                                />
                                                                <label className="form-check-label d-flex justify-content-between" htmlFor={`comp-${comp.component_id}`}>
                                                                    <div>
                                                                        <span className="fw-semibold">{comp.component_name}</span>
                                                                        <small className="text-muted d-block">
                                                                            Type: {comp.calculation_type} |
                                                                            Value: {comp.value || 'N/A'}
                                                                        </small>
                                                                    </div>
                                                                    <span className={`badge bg-${typeInfo.color} bg-opacity-25 text-${typeInfo.color}`}>
                                                                        {type}
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body">
                            <div className="d-flex flex-wrap gap-2">
                                <button
                                    className="btn btn-danger"
                                    onClick={exportToPDF}
                                    disabled={loading || !selectedPayroll || selectedComponents.length === 0}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-file-pdf me-2"></i>
                                            Export PDF
                                        </>
                                    )}
                                </button>

                                <button
                                    className="btn btn-success"
                                    onClick={exportToExcel}
                                    disabled={loading || !selectedPayroll || selectedComponents.length === 0}
                                >
                                    <i className="bi bi-file-excel me-2"></i>
                                    Export Excel
                                </button>

                                <button
                                    className="btn btn-warning"
                                    onClick={printReport}
                                    disabled={loading || !selectedPayroll || selectedComponents.length === 0}
                                >
                                    <i className="bi bi-printer me-2"></i>
                                    Print Report
                                </button>

                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => {
                                        setSelectedPayroll('');
                                        setSelectedComponents([]);
                                        setViewMode('list');
                                        setShowPreview(false);
                                        setEmployeesData([]);
                                        setCurrentTemplateId(null);
                                        fetchPayComponents();
                                        showAlertMessage('Form reset successfully!');
                                    }}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Company & Payroll Info */}
            {(companyInfo || payrollInfo) && showPreview && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body">
                                <div className="row">
                                    {companyInfo && (
                                        <div className="col-md-6">
                                            <h6 className="border-bottom pb-2">
                                                <i className="bi bi-building me-2"></i>
                                                Company Details
                                            </h6>
                                            <div className="row">
                                                <div className="col-6">
                                                    <small className="text-muted d-block">Company Name</small>
                                                    <div className="fw-bold">{companyInfo.company_name}</div>
                                                </div>
                                                <div className="col-6">
                                                    <small className="text-muted d-block">Company Code</small>
                                                    <div className="fw-bold">{companyInfo.company_code}</div>
                                                </div>
                                                <div className="col-12 mt-2">
                                                    <small className="text-muted d-block">Address</small>
                                                    <div>{companyInfo.address}, {companyInfo.city}, {companyInfo.state}, {companyInfo.country}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {payrollInfo && (
                                        <div className="col-md-6">
                                            <h6 className="border-bottom pb-2">
                                                <i className="bi bi-calendar me-2"></i>
                                                Payroll Details
                                            </h6>
                                            <div className="row">
                                                <div className="col-6">
                                                    <small className="text-muted d-block">Payroll ID</small>
                                                    <div className="fw-bold">{payrollInfo.payroll_id}</div>
                                                </div>
                                                <div className="col-6">
                                                    <small className="text-muted d-block">Month-Year</small>
                                                    <div className="fw-bold">{payrollInfo.month_year}</div>
                                                </div>
                                                <div className="col-6">
                                                    <small className="text-muted d-block">From Date</small>
                                                    <div>{payrollInfo.from_date}</div>
                                                </div>
                                                <div className="col-6">
                                                    <small className="text-muted d-block">To Date</small>
                                                    <div>{payrollInfo.to_date}</div>
                                                </div>
                                                <div className="col-6">
                                                    <small className="text-muted d-block">Status</small>
                                                    <div>
                                                        <span className={`badge bg-${payrollInfo.status === 'Closed' ? 'success' : payrollInfo.status === 'Processed' ? 'warning' : 'secondary'}`}>
                                                            {payrollInfo.status}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Data Preview Section - List View */}
            {showPreview && viewMode === 'list' && employeesData.length > 0 && (
                <div className="row" ref={previewRef}>
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">
                                        <i className="bi bi-table me-2 text-primary"></i>
                                        Payroll Preview - List View
                                        <span className="badge bg-primary ms-2">
                                            {employeesData.length} Employees
                                        </span>
                                        <span className="badge bg-secondary ms-2">
                                            {selectedComponents.length} Components
                                        </span>
                                    </h5>
                                </div>
                            </div>
                            <div className="card-body p-0">
                                {/* Data Table */}
                                <div className="table-responsive" style={{ maxHeight: '500px', overflow: 'auto' }}>
                                    <table className="table table-hover table-striped mb-0">
                                        <thead className="bg-light sticky-top">
                                            <tr>
                                                <th>#</th>
                                                <th>Emp Code</th>
                                                <th>Employee Name</th>
                                                <th>Department</th>
                                                <th>Designation</th>
                                                {selectedComponents.map(comp => (
                                                    <th key={comp} className="text-end">{comp}</th>
                                                ))}
                                                <th className="text-end">Total Earnings</th>
                                                <th className="text-end">Total Deductions</th>
                                                <th className="text-end">Net Pay</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {employeesData.map((employee, index) => {
                                                const summary = calculateSummary(employee);
                                                return (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>{employee.emp_code || ''}</td>
                                                        <td>{employee.name || ''}</td>
                                                        <td>{employee.department || ''}</td>
                                                        <td>{employee.designation || ''}</td>
                                                        {selectedComponents.map(comp => (
                                                            <td key={comp} className="text-end">
                                                                {parseFloat(employee[comp] || 0).toFixed(2)}
                                                            </td>
                                                        ))}
                                                        <td className="text-end fw-bold text-success">{summary.totalEarnings}</td>
                                                        <td className="text-end fw-bold text-danger">{summary.totalDeductions}</td>
                                                        <td className="text-end fw-bold text-primary">{summary.netPay}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot className="bg-light">
                                            <tr>
                                                <td colSpan={5} className="text-end fw-bold">TOTALS:</td>
                                                {selectedComponents.map(comp => {
                                                    const total = employeesData.reduce((sum, emp) =>
                                                        sum + parseFloat(emp[comp] || 0), 0
                                                    );
                                                    return <td key={comp} className="text-end fw-bold">{total.toFixed(2)}</td>;
                                                })}
                                                <td className="text-end fw-bold text-success">
                                                    {employeesData.reduce((sum, emp) => {
                                                        const summary = calculateSummary(emp);
                                                        return sum + parseFloat(summary.totalEarnings);
                                                    }, 0).toFixed(2)}
                                                </td>
                                                <td className="text-end fw-bold text-danger">
                                                    {employeesData.reduce((sum, emp) => {
                                                        const summary = calculateSummary(emp);
                                                        return sum + parseFloat(summary.totalDeductions);
                                                    }, 0).toFixed(2)}
                                                </td>
                                                <td className="text-end fw-bold text-primary">
                                                    {employeesData.reduce((sum, emp) => {
                                                        const summary = calculateSummary(emp);
                                                        return sum + parseFloat(summary.netPay);
                                                    }, 0).toFixed(2)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Data Preview Section - Card View */}
            {showPreview && viewMode === 'card' && employeesData.length > 0 && (
                <div className="row" ref={previewRef}>
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom">
                                <h5 className="mb-0">
                                    <i className="bi bi-card-text me-2 text-primary"></i>
                                    Payroll Preview - Card View
                                    <span className="badge bg-primary ms-2">
                                        {employeesData.length} Employees
                                    </span>
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    {employeesData.map((employee, index) => {
                                        const summary = calculateSummary(employee);
                                        return (
                                            <div key={index} className="col-md-6 col-lg-4 mb-4">
                                                <div className="card h-100 border shadow-sm employee-card">
                                                    <div className="card-header bg-light">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="mb-0 fw-bold">{employee.name || 'Employee'}</h6>
                                                                <small className="text-muted">{employee.emp_code} | {employee.department}</small>
                                                            </div>
                                                            <span className="badge bg-primary">{employee.designation}</span>
                                                        </div>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="row">
                                                            {selectedComponents.map(comp => (
                                                                <div key={comp} className="col-6 mb-2">
                                                                    <small className="text-muted d-block">{comp}</small>
                                                                    <div className="fw-bold text-end">
                                                                        {parseFloat(employee[comp] || 0).toFixed(2)}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="card-footer bg-white">
                                                        <div className="row">
                                                            <div className="col-4 text-center">
                                                                <small className="text-muted d-block">Earnings</small>
                                                                <div className="fw-bold text-success">{summary.totalEarnings}</div>
                                                            </div>
                                                            <div className="col-4 text-center">
                                                                <small className="text-muted d-block">Deductions</small>
                                                                <div className="fw-bold text-danger">{summary.totalDeductions}</div>
                                                            </div>
                                                            <div className="col-4 text-center">
                                                                <small className="text-muted d-block">Net Pay</small>
                                                                <div className="fw-bold text-primary">{summary.netPay}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Stats */}
            {showPreview && (
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card bg-light border-0">
                            <div className="card-body py-3">
                                <div className="row text-center">
                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <div className="fw-bold text-primary fs-4">{employeesData.length}</div>
                                        <small className="text-muted">Total Employees</small>
                                    </div>
                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <div className="fw-bold text-success fs-4">
                                            {employeesData.reduce((sum, emp) => {
                                                const summary = calculateSummary(emp);
                                                return sum + parseFloat(summary.totalEarnings);
                                            }, 0).toFixed(2)}
                                        </div>
                                        <small className="text-muted">Total Earnings</small>
                                    </div>
                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <div className="fw-bold text-danger fs-4">
                                            {employeesData.reduce((sum, emp) => {
                                                const summary = calculateSummary(emp);
                                                return sum + parseFloat(summary.totalDeductions);
                                            }, 0).toFixed(2)}
                                        </div>
                                        <small className="text-muted">Total Deductions</small>
                                    </div>
                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <div className="fw-bold text-dark fs-4">
                                            {employeesData.reduce((sum, emp) => {
                                                const summary = calculateSummary(emp);
                                                return sum + parseFloat(summary.netPay);
                                            }, 0).toFixed(2)}
                                        </div>
                                        <small className="text-muted">Total Net Pay</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .payroll-export-container .form-check-input:checked {
                    background-color: var(--bs-primary);
                    border-color: var(--bs-primary);
                }
                
                .payroll-export-container .card {
                    border-radius: 10px;
                }
                
                .payroll-export-container .list-group-item:hover {
                    background-color: rgba(0, 123, 255, 0.05);
                }
                
                .payroll-export-container .table th {
                    position: sticky;
                    top: 0;
                    background-color: #f8f9fa;
                    z-index: 10;
                }
                
                .payroll-export-container .table-responsive::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                
                .payroll-export-container .table-responsive::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                }
                
                .payroll-export-container .table-responsive::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 4px;
                }
                
                .payroll-export-container .table-responsive::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }
                
                .sticky-top {
                    position: -webkit-sticky;
                    position: sticky;
                    top: 0;
                    z-index: 1020;
                }
                
                .text-end {
                    text-align: right;
                }
                
                .employee-card {
                    transition: all 0.3s ease;
                }
                
                .employee-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
                }
                
                .alert {
                    animation: slideIn 0.3s ease;
                }
                
                @keyframes slideIn {
                    from {
                        transform: translateY(-20px);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
};

export default PayrollExport;